<script setup lang="ts">
/**
 * Tarjeta de anticipos de una cotización o de una venta de tienda.
 *
 * Nace como componente y no como un bloque pegado en la ficha porque la tienen que
 * ver **dos** pantallas: la ficha y el editor. Copiada en las dos, la tarjeta de la
 * ficha y la del editor divergirían —una pinta el saldo y la otra no, una admite
 * alta y la otra no— y nadie se enteraría hasta que las dos se vieran lado a lado.
 *
 * La lógica de los anticipos vive aquí dentro, incluida la del diálogo de alta: si
 * el editor la tuviera aparte, añadir un campo al formulario del anticipo sería
 * cambiarlo en dos sitios.
 *
 * `kind` decide contra qué recurso se escribe. Los anticipos de tienda siguen las
 * mismas reglas que los de cotización, pero cuelgan de otra ruta y de otro
 * permiso: reutilizar el componente evita que las dos pantallas se separen.
 */
import { computed, ref } from 'vue'
import AppCard from './AppCard.vue'
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Select from 'primevue/select'
import AppDialog from './Dialog.vue'
import { quoteApi, ADVANCE_PAYMENT_METHODS, type AdvancePaymentMethod, type QuoteAdvance } from '@/services/quotes'
import { storeSalesApi } from '@/services/storeSales'
import { formatCurrency } from '@/utils/format'

const props = withDefaults(defineProps<{
  /** Identificador de la cotización o de la venta. */
  quoteId: number
  /** Los anticipos que ya tiene. */
  advances: QuoteAdvance[]
  /** Suma de lo pagado, como la envía el servidor. */
  advancesTotal: string
  /** Lo que queda por cobrar. Va calculado por el servidor. */
  balance: string
  /** Documento al que pertenecen los anticipos. */
  kind?: 'quote' | 'store'
  /**
   * Si va metida dentro de otro contenedor, como la pestaña del editor.
   *
   * Cambia dos cosas. Una tarjeta dentro de otra se come sesenta píxeles de relleno
   * vertical entre el título y lo que hay debajo, y dentro del acordeón del cotizador
   * eso es aire de más: la variante apretada deja treinta y dos. Y el título **se
   * quita**: dentro del editor la pestaña de arriba ya se llama «Anticipos», y dos
   * veces el mismo nombre, una en grande y otra en pequeño, es repetir lo que ya se
   * está leyendo. La frase que explica qué es esa tarjeta sí se queda, porque esa no
   * la dice la pestaña.
   */
  anidada?: boolean
}>(), {
  kind: 'quote',
})

const emit = defineEmits<{
  /** El padre actualiza su copia de la cotización con las cifras del servidor. */
  'cambia': [totals: { advances_total: string; balance: string; advances: QuoteAdvance[] }]
}>()

const dialogVisible = ref(false)
const deleteVisible = ref(false)
const amount = ref<number | null>(null)
const paidOn = ref('')
const paymentMethod = ref<AdvancePaymentMethod | ''>('')
const note = ref('')
const errorMessage = ref<string | null>(null)
const saving = ref(false)
const toDelete = ref<QuoteAdvance | null>(null)

/** «esta venta» o «esta cotización», para que los textos no mientan. */
const documento = computed(() => props.kind === 'store' ? 'esta venta' : 'esta cotización')

/** Prefijo de los `data-testid`, para no mezclar las pruebas de los dos documentos. */
const testId = computed(() => props.kind === 'store' ? 'store-sale' : 'quote')

/** Saldo a favor: el cliente pagó más de lo que costaba. */
const hasFavor = computed(() => Number(props.balance) < 0)

/** El saldo en positivo, para no enseñar un número con el signo cambiado. */
const favorAmount = computed(() => Math.abs(Number(props.balance)))

/**
 * La fecha de hoy, en el formato que espera el campo de fecha.
 *
 * Puesta por defecto porque un anticipo casi siempre se anota el mismo día que se
 * cobra, y salir con el campo vacío obliga a abrir el calendario para escribir una
 * fecha que ya se sabe.
 */
function hoy(): string {
  const ahora = new Date()
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')

  return `${ahora.getFullYear()}-${mes}-${dia}`
}

function abrir(): void {
  amount.value = null
  paidOn.value = hoy()
  paymentMethod.value = ''
  note.value = ''
  errorMessage.value = null
  dialogVisible.value = true
}

async function guardar(): Promise<void> {
  saving.value = true
  errorMessage.value = null

  try {
    const payload = {
      amount: Number(amount.value ?? 0),
      paid_on: paidOn.value,
      payment_method: paymentMethod.value as AdvancePaymentMethod,
      note: note.value.trim() === '' ? null : note.value.trim(),
    }

    const { data } = props.kind === 'store'
      ? await storeSalesApi.createAdvance(props.quoteId, payload)
      : await quoteApi.createAdvance(props.quoteId, payload)

    emit('cambia', {
      advances_total: data.data.advances_total,
      balance: data.data.balance,
      advances: [...props.advances, data.data.advance],
    })
    dialogVisible.value = false
  } catch (cause: unknown) {
    const maybe = cause as {
      response?: { data?: { error?: { message?: string; details?: { fields?: Record<string, string[]> } } } }
    }
    const campos = maybe.response?.data?.error?.details?.fields

    // Al campo que hay que corregir: importes inválidos y fechas futuras se
    // arreglan en esta misma pantalla, no con un aviso arriba.
    errorMessage.value = campos?.amount?.[0]
      ?? campos?.paid_on?.[0]
      ?? campos?.payment_method?.[0]
      ?? maybe.response?.data?.error?.message
      ?? 'No se pudo registrar el anticipo.'
  } finally {
    saving.value = false
  }
}

function pedirBorrar(advance: QuoteAdvance): void {
  toDelete.value = advance
  deleteVisible.value = true
}

async function borrar(): Promise<void> {
  if (toDelete.value === null) return

  saving.value = true
  errorMessage.value = null

  try {
    const { data } = props.kind === 'store'
      ? await storeSalesApi.deleteAdvance(props.quoteId, toDelete.value.id)
      : await quoteApi.deleteAdvance(props.quoteId, toDelete.value.id)

    emit('cambia', {
      advances_total: data.data.advances_total,
      balance: data.data.balance,
      advances: props.advances.filter((avance) => avance.id !== toDelete.value?.id),
    })

    toDelete.value = null
    deleteVisible.value = false
  } catch (cause: unknown) {
    const maybe = cause as { response?: { data?: { error?: { message?: string } } } }
    errorMessage.value = maybe.response?.data?.error?.message ?? 'No se pudo borrar el anticipo.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <AppCard :class="anidada ? 'card-apretada' : ''">
    <template #header>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            v-if="!anidada"
            class="text-lg font-semibold text-surface-900 dark:text-surface-100"
          >
            Anticipos
          </h2>
          <p
            :class="anidada ? '' : 'mt-0.5'"
            class="text-sm text-surface-500 dark:text-surface-400"
          >
            Pagos que el cliente ya ha hecho sobre {{ documento }}
          </p>
        </div>
        <Button
          label="Registrar anticipo"
          icon="pi pi-plus"
          size="small"
          outlined
          :data-testid="`${testId}-add-advance`"
          @click="abrir"
        />
      </div>
    </template>

    <div class="space-y-4">
      <!--
        El total pagado y el saldo van juntos y siempre, incluso sin anticipos: quien
        abre la pantalla necesita ver cuánto falta por cobrar de una vez, y tener que
        restar mentalmente es justo lo que hay que evitar.
      -->
      <dl
        class="grid grid-cols-2 gap-4"
        :data-testid="`${testId}-advances-summary`"
      >
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            Anticipos
          </dt>
          <dd
            :data-testid="`${testId}-advances-total`"
            class="text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-300"
          >
            {{ formatCurrency(advancesTotal) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm text-surface-500 dark:text-surface-400">
            {{ hasFavor ? 'Saldo a favor' : 'Saldo por cobrar' }}
          </dt>
          <dd
            :data-testid="`${testId}-balance`"
            class="text-lg font-semibold tabular-nums"
            :class="hasFavor
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-surface-900 dark:text-surface-100'"
          >
            {{ formatCurrency(hasFavor ? String(favorAmount) : balance) }}
          </dd>
        </div>
      </dl>

      <Message
        v-if="errorMessage !== null"
        severity="error"
        :closable="true"
        variant="simple"
        role="alert"
        :data-testid="kind === 'store' ? 'store-sale-advance-error' : 'advance-error'"
        @close="errorMessage = null"
      >
        {{ errorMessage }}
      </Message>

      <ul
        v-if="advances.length > 0"
        class="divide-y divide-surface-100 dark:divide-surface-800"
        :data-testid="`${testId}-advances-list`"
      >
        <li
          v-for="advance in advances"
          :key="advance.id"
          class="flex items-start justify-between gap-3 py-2.5 first:pt-0"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium tabular-nums text-surface-900 dark:text-surface-100">
              {{ formatCurrency(advance.amount) }}
            </p>
            <p class="text-xs text-surface-500 dark:text-surface-400">
              {{ advance.payment_method_label ?? advance.payment_method }}
              <span v-if="advance.paid_on"> · {{ advance.paid_on }}</span>
            </p>
            <p
              v-if="advance.note"
              class="mt-0.5 break-words text-xs text-surface-500 dark:text-surface-400"
            >
              {{ advance.note }}
            </p>
          </div>
          <button
            type="button"
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-surface-500 transition-colors hover:bg-surface-100 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-surface-800"
            :aria-label="`Borrar el anticipo de ${formatCurrency(advance.amount)}`"
            @click="pedirBorrar(advance)"
          >
            <i
              class="pi pi-trash"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>

      <!--
        La tarjeta se ve siempre, y sin anticipos dice que no hay ninguno. Si solo
        apareciera cuando hay algo, no se sabría que se puede registrar el primero: es
        el mismo fallo que el campo de línea de atención en el editor, que se comía
        porque se escondía cuando estaba vacío.
      -->
      <p
        v-else
        class="py-2 text-sm text-surface-500 dark:text-surface-400"
        :data-testid="`${testId}-advances-empty`"
      >
        Todavía no hay anticipos en {{ documento }}.
      </p>
    </div>
  </AppCard>

  <AppDialog
    v-model:visible="dialogVisible"
    title="Registrar anticipo"
    confirm-label="Registrar"
    @confirm="guardar"
  >
    <div class="space-y-4">
      <div>
        <label
          for="advance-amount"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Importe
        </label>
        <InputNumber
          v-model="amount"
          input-id="advance-amount"
          mode="currency"
          currency="COP"
          locale="es-CO"
          :min="0"
          :max-fraction-digits="2"
          class="mt-1 w-full"
        />
      </div>

      <div>
        <label
          for="advance-date"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Fecha del pago
        </label>
        <input
          id="advance-date"
          v-model="paidOn"
          type="date"
          class="mt-1 w-full rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 dark:border-surface-600 dark:bg-surface-900 dark:text-surface-100"
        >
      </div>

      <div>
        <label
          for="advance-method"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Medio de pago
        </label>
        <Select
          v-model="paymentMethod"
          input-id="advance-method"
          :options="ADVANCE_PAYMENT_METHODS"
          option-label="label"
          option-value="value"
          placeholder="Seleccione el medio de pago"
          class="mt-1 w-full"
        />
      </div>

      <div>
        <label
          for="advance-note"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Nota
        </label>
        <InputText
          id="advance-note"
          v-model="note"
          placeholder="Opcional: para qué era este pago"
          class="mt-1 w-full"
        />
      </div>

      <p class="text-sm text-surface-500 dark:text-surface-400">
        El total del documento no cambia: el anticipo se registra aparte y lo que
        se descuenta es el saldo por cobrar.
      </p>
    </div>

    <!--
      Pie propio en vez del de por defecto, para poder poner el botón a trabajar
      mientras guarda. Registrar dos veces el mismo anticipo por impaciencia es un
      error que el servidor no puede arreglar: ya le llegó el dinero una vez.
    -->
    <template #footer>
      <Button
        label="Cancelar"
        severity="secondary"
        @click="dialogVisible = false"
      />
      <Button
        label="Registrar"
        :loading="saving"
        @click="guardar"
      />
    </template>
  </AppDialog>

  <AppDialog
    v-model:visible="deleteVisible"
    title="Borrar anticipo"
    confirm-label="Borrar"
    @confirm="borrar"
    @cancel="toDelete = null"
  >
    <p class="text-surface-700 dark:text-surface-300">
      Se va a borrar el anticipo de
      <strong>{{ toDelete ? formatCurrency(toDelete.amount) : '' }}</strong>
      y el saldo volverá a incluirlo como pendiente.
    </p>

    <template #footer>
      <Button
        label="Cancelar"
        severity="secondary"
        @click="deleteVisible = false"
      />
      <Button
        label="Borrar"
        severity="danger"
        :loading="saving"
        @click="borrar"
      />
    </template>
  </AppDialog>
</template>